import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  GeographicalRegion,
  getInitGeographicalRegion,
} from '../../../models/geographical-region.model';
import { GeographicalRegionService } from '../../../services/geographical-region.service';
import { ToastrService } from 'ngx-toastr';
import { UtilitySharedService } from '../../../services/shared//utility-shared.service';
import { PermissionsService } from '../../../services/permissions.service';

@Component({
  selector: 'lib-nec-region',
  standalone: false,
  templateUrl: './nec-region.component.html',
  styleUrls: ['./nec-region.component.scss', '../network-entity-classes.component.scss'],
})
export class NecRegionComponent implements OnInit {
  @ViewChild('closeEditModal') closeEditModal!: ElementRef;
  filterOpened: boolean = false;
  searchDropdownOpened: boolean = false;
  selectedId: number = 0;
  selectedRegion: GeographicalRegion = getInitGeographicalRegion();
  actionMenuOpenForId: number | null = null; //for the popup, only to be shown on the row of the action menu that it is selected for
  currentPage: number = 1;
  pageSize: number = 10;
  pageSizes = [10, 20];
  count: number = 0;
  searchText: string = '';
  regions: GeographicalRegion[] = [];
  utilityId: number = 0;
  isEdit: boolean = false;
  regionToSave: GeographicalRegion = getInitGeographicalRegion();

  constructor(
    private geographicalRegionService: GeographicalRegionService,
    private toastr: ToastrService,
    private utilitySharedService: UtilitySharedService,
    public permissionsService: PermissionsService
  ) 
  {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.getAllGeographicalRegions();
  }

  toggleActionMenu(region: GeographicalRegion, event: MouseEvent) {
    event.stopPropagation();
    this.selectedId = region.id!;
    this.selectedRegion = region;
    this.actionMenuOpenForId =
      this.actionMenuOpenForId === region.id ? null : region.id;
  }

  onRemove() {
    this.geographicalRegionService.delete(this.selectedRegion.id!).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success(responseData?.message);
          this.reloadTable();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
    this.actionMenuOpenForId = null;
  }

  @HostListener('document:click')
  closeActionMenu() {
    if (this.actionMenuOpenForId !== null) {
      this.actionMenuOpenForId = null;
    }
  }

  private getAllGeographicalRegions() {
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: this.searchText,
      },
      utilityId: this.utilitySharedService.getUtilityId(),
    };
    this.geographicalRegionService.getAll(dataIn).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data.data;
          this.count = responseData.data.count;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.reloadTable()
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.currentPage = 1;
    this.reloadTable()
  }

  reloadTable(){
    this.getAllGeographicalRegions();
  }

  openAddModal(){
    this.resetInputFields();
    this.isEdit = false;
  }

  selectRegion(region: GeographicalRegion){
    this.selectedRegion = region;
    this.openEditModal();
  }

  openEditModal(){
    this.resetInputFields();
    this.isEdit = true;
    this.fillInputFields();
  }

  updatedName: string = '';
  updatedCode: string = '';
  updatedNumber: string = '';
  updatedLatitude!: number | null;
  updatedLongitude!: number | null;

  private fillInputFields() {
    this.updatedName = this.selectedRegion.name;
    this.updatedCode = this.selectedRegion.code;
    this.updatedNumber = this.selectedRegion.number;
    this.updatedLatitude = this.selectedRegion.latitude;
    this.updatedLongitude = this.selectedRegion.longitude;
  }

  private resetInputFields(){
    this.updatedName = '';
    this.updatedCode = '';
    this.updatedNumber = '';
    this.updatedLatitude = null;
    this.updatedLongitude = null;
  }

  private mapRegion() {
    this.regionToSave.id = this.isEdit ? this.selectedRegion.id : null;
    this.regionToSave.utilityId = this.utilityId;
    this.regionToSave.name = this.updatedName;
    this.regionToSave.code = this.updatedCode;
    this.regionToSave.number = this.updatedNumber;
    this.regionToSave.latitude = this.updatedLatitude;
    this.regionToSave.longitude = this.updatedLongitude;
  }

  save(){
    const isNumberValid = /^7\d{3}$/.test(this.updatedNumber);
    var isLatitudeValid = true;
    var isLongitudeValid = true;
    if(this.updatedLatitude !== null && this.updatedLatitude !== undefined)
      isLatitudeValid = /^-?(90(\.0+)?|[0-8]?\d(\.\d+)?)$/.test(this.updatedLatitude.toString());
    if(this.updatedLongitude !== null && this.updatedLongitude !== undefined)
      isLongitudeValid = /^-?(?:180(?:\.0+)?|1[0-7]\d(?:\.\d+)?|[1-9]\d(?:\.\d+)?|\d(?:\.\d+)?)$/.test(this.updatedLongitude.toString());

    if(this.updatedName === ''){
      this.toastr.warning("Name can't be empty!")
    } else if (this.updatedCode === '') {
      this.toastr.warning("Code can't be empty!")
    } else if (this.updatedNumber === '') {
      this.toastr.warning("Number can't be empty!")
    } else if (!isNumberValid) {
      this.toastr.warning("Number must be 4 digits and to start with 7!")
    } else if (!isLatitudeValid) {
        this.toastr.warning("Latitude must be between -90 and 90!")
    } else if (!isLongitudeValid) {
        this.toastr.warning("Longitude must be between -180 and 180!")
    }  
    else{
      this.actionMenuOpenForId = null;
      this.mapRegion();
      this.geographicalRegionService.save(this.regionToSave).subscribe({
        next: (response) => {
          if (response.body.status === 200 || response.body.status === 'OK') {
            this.closeEditModal?.nativeElement?.click();
            this.toastr.success(response.body.message);
            this.reloadTable();
          } else {
            this.toastr.error(response.body.message || 'Failed to save region');
          }
        },
        error: (_) => {
          this.toastr.error('Error occurred while saving region');
        },
      });
    }
  }
}
